/**
 * Privacy-preserving handoff from the free, unauthenticated text-message court formatter
 * (`TextMessageCourtFormatter.tsx`, mounted standalone at /tools/print-text-messages-for-court)
 * into the real, authenticated FenzSafe workspace (Issue #114 founder direction, comment
 * 5774592884, CE priority #6: "Connect free utilities such as the text-message formatter to
 * 'Add this to my FenzSafe matter.'").
 *
 * The formatter is a completely separate, unauthenticated mount point with no session, matter, or
 * store context of its own -- it cannot safely call the real FenzSafe document-upload contract
 * directly without inventing a new unauthenticated write path into a customer's tenant data, which
 * is exactly the kind of new architecture the founder's own direction says not to build here. The
 * smallest safe handoff is a same-tab, same-origin `sessionStorage` note: written only when the
 * visitor explicitly clicks "Add this to my FenzSafe matter" (never on paste/format/print), read
 * once by the authenticated `FenzSafeApp.tsx` if/when the visitor is signed in to a FenzSafe
 * workspace, and only ever turned into a real uploaded document by a second explicit click there
 * ("Add to this matter"). Nothing here performs a network request -- both sides are pure local
 * storage read/write, so the formatter's "nothing is uploaded unless you choose to" promise holds
 * all the way through: sessionStorage never leaves the browser, isn't sent to any server, and is
 * cleared when the tab closes.
 *
 * Fails closed by design: if sessionStorage is unavailable (private browsing, blocked storage) the
 * write/read/clear below silently no-op rather than throwing, and the formatter keeps working
 * standalone either way -- the same guarantee `fenzsafeFunnelEvents.ts`'s own best-effort tracking
 * already relies on for this same reason.
 */

export type PendingTextMessageHandoff = {
  /** Suggested file name for the uploaded document -- carries no message content itself. */
  fileName: string;
  /** The exact "Text Message Record" text the formatter displayed/printed -- see TextMessageCourtFormatter.tsx's buildRecordText(). */
  documentText: string;
  caseName: string;
  otherParty: string;
  generatedAt: string;
  hash: string;
};

const STORAGE_KEY = 'fenzsafe.pendingTextMessageHandoff.v1';

export function writePendingTextMessageHandoff(payload: PendingTextMessageHandoff): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Best-effort -- see this module's own doc comment on failing closed.
  }
}

export function readPendingTextMessageHandoff(): PendingTextMessageHandoff | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingTextMessageHandoff>;
    if (
      typeof parsed.fileName !== 'string' ||
      typeof parsed.documentText !== 'string' ||
      typeof parsed.generatedAt !== 'string' ||
      typeof parsed.hash !== 'string'
    ) {
      return null;
    }
    return {
      fileName: parsed.fileName,
      documentText: parsed.documentText,
      caseName: typeof parsed.caseName === 'string' ? parsed.caseName : '',
      otherParty: typeof parsed.otherParty === 'string' ? parsed.otherParty : '',
      generatedAt: parsed.generatedAt,
      hash: parsed.hash,
    };
  } catch {
    return null;
  }
}

export function clearPendingTextMessageHandoff(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort -- see this module's own doc comment on failing closed.
  }
}
